import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "mergeButton",
    "mergeButtonText",
    "checkbox",
    "suppliersGrid",
    "mergeModal",
    "modalTitle",
    "modalBody",
    "selectedList",
    "primarySupplier",
    "confirmButton",
    "cardWrapper",
    "selectionOverlay",
    "selectionIndicator",
    "fusionControls",
    "selectionSummary",
  ];

  connect() {
    this.selectedSuppliers = new Map();
    this.mergeModeActive = false;

    document.addEventListener(
      "turbo:frame-load",
      this.handleTurboFrameLoad.bind(this)
    );
  }

  disconnect() {
    document.removeEventListener(
      "turbo:frame-load",
      this.handleTurboFrameLoad.bind(this)
    );
    this.clearPersistedState();
  }

  handleTurboFrameLoad(event) {
    // When new content is loaded via Turbo (like pagination), apply selection state
    if (this.mergeModeActive && this.selectedSuppliers.size > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.applyVisualStateToCurrentPage();
      }, 50);
    }
  }

  enterMergeModeFromHeader() {
    this.enterMergeMode();
  }
  // Note: W5-8 (RES-112) removed the per-card "Fusionner avec d'autres" entry
  // point. Merge mode is now entered exclusively from the page header button.
  // The pre-select-from-card path (former startFusionMode) is gone.

  enterMergeMode() {
    this.mergeModeActive = true;

    if (this.hasSuppliersGridTarget) {
      this.suppliersGridTarget.classList.add("merge-mode-active");
    }

    // Show fusion controls
    this.fusionControlsTarget.classList.remove("hidden");

    // Update button text
    this.updateMergeButton();

  }

  exitMergeMode() {
    if (!this.mergeModeActive) return;

    this.mergeModeActive = false;

    // Uncheck all hidden checkboxes
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // Hide all selection overlays and indicators
    this.selectionOverlayTargets.forEach((overlay) => {
      overlay.classList.remove("opacity-100", "visible");
      overlay.classList.add("opacity-0", "invisible");
    });

    this.selectionIndicatorTargets.forEach((indicator) => {
      indicator.classList.remove("opacity-100", "visible");
      indicator.classList.add("opacity-0", "invisible");
    });

    if (this.hasSuppliersGridTarget) {
      this.suppliersGridTarget.classList.remove("merge-mode-active");
    }

    // Hide fusion controls
    this.fusionControlsTarget.classList.add("hidden");

    // Clear selections
    this.selectedSuppliers.clear();

    // Clear persisted state
    this.clearPersistedState();
  }

  toggleSupplierSelection(event) {
    // Only handle clicks when in merge mode
    if (!this.mergeModeActive) return;

    // Prevent clicks when target is a link or button inside the card
    if (event.target.closest("a, button")) return;

    const cardWrapper = event.currentTarget;
    const supplierId = cardWrapper.dataset.supplierId;
    const supplierName = cardWrapper.dataset.supplierName;

    // Find the associated elements
    const checkbox = cardWrapper.querySelector(
      '[data-supplier-merge-target="checkbox"]'
    );
    const overlay = cardWrapper.querySelector(
      '[data-supplier-merge-target="selectionOverlay"]'
    );
    const indicator = cardWrapper.querySelector(
      '[data-supplier-merge-target="selectionIndicator"]'
    );

    // Toggle selection
    const isCurrentlySelected = this.selectedSuppliers.has(supplierId);

    if (isCurrentlySelected) {
      // Deselect
      this.selectedSuppliers.delete(supplierId);
      checkbox.checked = false;

      // Hide visual indicators
      overlay?.classList.remove("opacity-100", "visible");
      overlay?.classList.add("opacity-0", "invisible");
      indicator?.classList.remove("opacity-100", "visible");
      indicator?.classList.add("opacity-0", "invisible");
    } else {
      // Select
      this.selectedSuppliers.set(supplierId, {
        id: supplierId,
        name: supplierName,
      });
      checkbox.checked = true;

      // Show visual indicators
      overlay?.classList.remove("opacity-0", "invisible");
      overlay?.classList.add("opacity-100", "visible");
      indicator?.classList.remove("opacity-0", "invisible");
      indicator?.classList.add("opacity-100", "visible");
    }

    this.updateMergeButton();
  }

  preselectSupplier(supplierId, supplierName) {
    // Find the card wrapper for this supplier
    const cardWrappers = this.cardWrapperTargets;

    cardWrappers.forEach((cardWrapper) => {
      if (cardWrapper.dataset.supplierId === supplierId) {
        // Select this supplier
        this.selectedSuppliers.set(supplierId, {
          id: supplierId,
          name: supplierName,
        });

        // Update visual indicators
        const checkbox = cardWrapper.querySelector(
          '[data-supplier-merge-target="checkbox"]'
        );
        const overlay = cardWrapper.querySelector(
          '[data-supplier-merge-target="selectionOverlay"]'
        );
        const indicator = cardWrapper.querySelector(
          '[data-supplier-merge-target="selectionIndicator"]'
        );

        if (checkbox) checkbox.checked = true;

        if (overlay) {
          overlay.classList.remove("opacity-0", "invisible");
          overlay.classList.add("opacity-100", "visible");
        }

        if (indicator) {
          indicator.classList.remove("opacity-0", "invisible");
          indicator.classList.add("opacity-100", "visible");
        }
      }
    });

    this.updateMergeButton();
  }

  applyVisualStateToCurrentPage() {
    // Apply selection state to any suppliers that are currently visible on the page
    this.cardWrapperTargets.forEach((cardWrapper) => {
      const supplierId = cardWrapper.dataset.supplierId;

      if (this.selectedSuppliers.has(supplierId)) {
        // This supplier should be selected
        const checkbox = cardWrapper.querySelector(
          '[data-supplier-merge-target="checkbox"]'
        );
        const overlay = cardWrapper.querySelector(
          '[data-supplier-merge-target="selectionOverlay"]'
        );
        const indicator = cardWrapper.querySelector(
          '[data-supplier-merge-target="selectionIndicator"]'
        );

        if (checkbox) checkbox.checked = true;

        if (overlay) {
          overlay.classList.remove("opacity-0", "invisible");
          overlay.classList.add("opacity-100", "visible");
        }

        if (indicator) {
          indicator.classList.remove("opacity-0", "invisible");
          indicator.classList.add("opacity-100", "visible");
        }
      }
    });
  }

  clearPersistedState() {
    sessionStorage.removeItem("supplierMergeState");
  }

  updateMergeButton() {
    const count = this.selectedSuppliers.size;

    if (this.hasSelectionSummaryTarget) {
      this.selectionSummaryTarget.textContent =
        count === 0
          ? "Aucun fournisseur sélectionné"
          : `${count} fournisseur(s) sélectionné(s)`;
    }

    if (count >= 2) {
      this.mergeButtonTarget.classList.remove("btn-disabled");
      this.mergeButtonTarget.disabled = false;
      this.mergeButtonTextTarget.textContent = `Fusionner ${count} fournisseurs`;
    } else {
      this.mergeButtonTarget.classList.add("btn-disabled");
      this.mergeButtonTarget.disabled = true;
      if (count === 1) {
        this.mergeButtonTextTarget.textContent =
          "Sélectionner un autre fournisseur";
      } else {
        this.mergeButtonTextTarget.textContent = "Fusionner";
      }
    }
  }

  showMergeModal() {
    if (this.selectedSuppliers.size < 2) {
      return;
    }

    this.populateModalContent();
    this.mergeModalTarget.showModal();
  }

  populateModalContent() {
    const suppliers = Array.from(this.selectedSuppliers.values());
    const count = suppliers.length;
    const deletedCount = Math.max(count - 1, 0);

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = `Fusionner ${count} fournisseurs`;
    }

    if (this.hasModalBodyTarget) {
      this.modalBodyTarget.innerHTML = `
        <strong>${deletedCount} fournisseur(s) seront supprimés.</strong>
        Leurs factures restent et sont rattachées au fournisseur principal sélectionné.
        Cette action est irréversible.
      `;
    }

    this.selectedListTarget.innerHTML = suppliers
      .map(
        (supplier) =>
          `<div class="flex items-center gap-3 p-2 bg-base-100 rounded-lg border border-base-200">
        <div class="w-2 h-2 rounded-full bg-primary shrink-0"></div>
        <span class="font-medium">${supplier.name}</span>
      </div>`
      )
      .join("");

    this.primarySupplierTarget.innerHTML = `
      <option disabled selected>Choisir le fournisseur principal…</option>
      ${suppliers
        .map(
          (supplier) =>
            `<option value="${supplier.id}">${supplier.name}</option>`
        )
        .join("")}
    `;
  }

  closeModal() {
    this.mergeModalTarget.close();
  }

  async confirmMerge() {
    const primarySupplierId = this.primarySupplierTarget.value;

    if (!primarySupplierId) {
      alert("Veuillez sélectionner un fournisseur principal.");
      return;
    }

    // Disable the confirm button to prevent double-clicks
    this.confirmButtonTarget.disabled = true;
    this.confirmButtonTarget.innerHTML = `
      <span class="loading loading-spinner loading-sm"></span>
      Fusion en cours...
    `;

    try {
      const response = await fetch(this.getMergeUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
        },
        body: JSON.stringify({
          primary_supplier_id: primarySupplierId,
          supplier_ids: Array.from(this.selectedSuppliers.keys()),
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Show success message
        this.showSuccessMessage(
          result.message,
          result.merged_supplier,
          result.deleted_count
        );

        // Close modal and reset
        this.closeModal();
        this.resetConfirmButton();
        this.exitMergeMode();

        // Clear persisted state since merge is complete
        this.clearPersistedState();

        // Refresh the page to show updated supplier list
        window.location.reload();
      } else {
        // Show error message
        this.showErrorMessage(result.message);
        this.resetConfirmButton();
      }
    } catch (error) {
      console.error("Merge request failed:", error);
      this.showErrorMessage(
        "Une erreur inattendue s'est produite. Veuillez réessayer."
      );
      this.resetConfirmButton();
    }
  }

  getMergeUrl() {
    // Extract establishment ID from current URL
    const pathParts = window.location.pathname.split("/");
    const establishmentIndex = pathParts.indexOf("establishments");
    if (establishmentIndex !== -1 && pathParts[establishmentIndex + 1]) {
      const establishmentId = pathParts[establishmentIndex + 1];
      return `/establishments/${establishmentId}/suppliers/merge`;
    }
    throw new Error("Could not determine establishment ID from URL");
  }

  getCsrfToken() {
    return document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
  }

  showSuccessMessage(message, mergedSupplier, deletedCount) {
    // Create a temporary success alert
    const alert = document.createElement("div");
    alert.className = "alert alert-success mb-4";
    alert.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
      <span>${message} ${deletedCount} fournisseur(s) fusionné(s) dans "${mergedSupplier.name}".</span>
    `;

    // Insert at the top of the page
    const container = document.querySelector(".p-6");
    if (container) {
      container.insertBefore(alert, container.firstChild);

      // Remove after 5 seconds
      setTimeout(() => alert.remove(), 5000);
    }
  }

  showErrorMessage(message) {
    alert(`Erreur: ${message}`);
  }

  resetConfirmButton() {
    this.confirmButtonTarget.disabled = false;
    this.confirmButtonTarget.textContent = "Fusionner";
  }
}
